import { format } from 'date-fns';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { useDateFnsLocale } from '@/hooks/use-date-fns-locale';
import { capitalizeFirst } from '@/lib/capitalize-first';

/** Compact chip label, e.g. "mar. 14 juil." — trailing dot trimmed. */
const CHIP_DATE_PATTERN = 'EEE d MMM';

/** Full date announced to screen readers on the remove button. */
const FULL_DATE_PATTERN = 'd MMMM yyyy';

/**
 * Chips of the dates picked in a slot-creation form, with a per-date remove and
 * a "clear all" shortcut. Renders a dashed empty state when nothing is picked.
 *
 * @param props.dates - the picked dates, in any order
 * @param props.periodLabel - period + hours the dates will be created on
 * @param props.onRemove - drops one date
 * @param props.onClear - drops every date
 * @returns the selected-dates card element
 */
export const SelectedDatesCard = ({
  dates,
  periodLabel,
  onRemove,
  onClear,
}: {
  dates: Date[];
  periodLabel?: string;
  onRemove: (date: Date) => void;
  onClear: () => void;
}) => {
  const { t } = useTranslation();
  const locale = useDateFnsLocale();

  if (dates.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-md border border-dashed bg-muted/30 px-4 py-6 text-muted-foreground text-sm">
        {t('slot-sheet.selected-dates.empty')}
      </div>
    );
  }

  const sorted = [...dates].sort((a, b) => a.getTime() - b.getTime());

  return (
    <div className="space-y-2 rounded-md border bg-card p-3">
      <header className="flex items-center justify-between gap-2">
        <p className="font-medium text-muted-foreground text-xs">
          {t('slot-sheet.selected-dates.title')}{' '}
          <span className="text-foreground tabular-nums">({dates.length})</span>
          {periodLabel && <span> · {periodLabel}</span>}
        </p>
        <button
          type="button"
          onClick={onClear}
          className="shrink-0 whitespace-nowrap text-muted-foreground text-xs hover:text-foreground hover:underline"
        >
          {t('slot-sheet.selected-dates.clear')}
        </button>
      </header>

      <ul className="flex flex-wrap gap-1.5">
        {sorted.map((date) => (
          <li key={date.toISOString()}>
            <Badge
              variant="secondary"
              className="gap-1 py-0.5 pr-1 pl-2 tabular-nums"
            >
              {capitalizeFirst(
                format(date, CHIP_DATE_PATTERN, { locale }).replace(/\.$/, ''),
              )}
              <button
                type="button"
                onClick={() => onRemove(date)}
                aria-label={t('slot-sheet.selected-dates.remove', {
                  date: format(date, FULL_DATE_PATTERN, { locale }),
                })}
                className="ml-0.5 inline-flex size-4 items-center justify-center rounded-sm text-muted-foreground hover:bg-background hover:text-foreground"
              >
                <XIcon
                  className="size-3"
                  aria-hidden="true"
                />
              </button>
            </Badge>
          </li>
        ))}
      </ul>
    </div>
  );
};
